import type { Edulead } from 'edulead';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { allPages, toastV4Error, v4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import { HUBSPOT_COMPANY_FIELDS, HUBSPOT_CONTACT_FIELDS } from '../utils/hubspot-field-config';
import { CrmFieldMappings, CrmProperty, MappingChange, crmPropertyOf, mappingList, mappingRecord } from './CrmFieldMappings';

interface FieldMapping {
  eduleadField: string;
  hubspotField: string;
  objectType?: 'CONTACT' | 'INSTITUTION';
}

interface HubspotFieldMappingsProps {
  onSaveSuccess?: () => void;
  teamId: string;
  /** When true, displays mappings in read-only mode without editing controls */
  readOnly?: boolean;
  /** Pre-loaded mappings data (used in readOnly mode to skip API call) */
  mappings?: FieldMapping[];
  /** The HubSpot integration the page already read, to avoid a second GetIntegration */
  integration?: Edulead.EduleadV4Integration;
}

export function HubspotFieldMappings({ onSaveSuccess, teamId, readOnly = false, mappings: initialMappings, integration }: HubspotFieldMappingsProps) {
  const [companyFieldMappings, setCompanyFieldMappings] = useState<Record<string, string>>({});
  const [contactFieldMappings, setContactFieldMappings] = useState<Record<string, string>>({});
  // Track original state for diff computation
  const [originalCompanyFieldMappings, setOriginalCompanyFieldMappings] = useState<Record<string, string>>({});
  const [originalContactFieldMappings, setOriginalContactFieldMappings] = useState<Record<string, string>>({});
  const [availableCompanyProperties, setAvailableCompanyProperties] = useState<CrmProperty[]>([]);
  const [availableContactProperties, setAvailableContactProperties] = useState<CrmProperty[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [propertiesLoadError, setPropertiesLoadError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);

  useEffect(() => {
    // If readOnly with pre-loaded mappings, set them first then load HubSpot properties for validation
    if (readOnly && initialMappings) {
      const companyMappings: Record<string, string> = {};
      const contactMappings: Record<string, string> = {};
      initialMappings.forEach(m => {
        if (m.objectType === 'INSTITUTION') {
          companyMappings[m.eduleadField] = m.hubspotField;
        } else if (m.objectType === 'CONTACT') {
          contactMappings[m.eduleadField] = m.hubspotField;
        }
      });
      setCompanyFieldMappings(companyMappings);
      setContactFieldMappings(contactMappings);
      setOriginalCompanyFieldMappings(companyMappings);
      setOriginalContactFieldMappings(contactMappings);
      // Still load HubSpot properties for validation
      loadHubspotProperties();
    } else {
      loadHubspotData();
    }
  }, [readOnly, initialMappings]);

  // Load only HubSpot properties (for read-only mode validation). Only boss's customer CRM tab
  // passes `mappings`, for a customer's team: staff read it by naming that team in Edulead-Team,
  // which the request sets instead of the client's own (the staff team).
  async function loadHubspotProperties() {
    try {
      setIsLoading(true);
      setPropertiesLoadError(null);
      const properties = await allPages(
        eduleadV4.integrations.listProperties({ integration_id: 'hubspot', page_size: 500 }, { team: teamId })
      );
      setAvailableContactProperties(
        properties.filter(p => p.object_type === 'INTEGRATION_OBJECT_TYPE_CONTACT').map(p => crmPropertyOf(p, 'hubspot'))
      );
      setAvailableCompanyProperties(
        properties.filter(p => p.object_type === 'INTEGRATION_OBJECT_TYPE_INSTITUTION').map(p => crmPropertyOf(p, 'hubspot'))
      );
    } catch (error) {
      setPropertiesLoadError(v4Error(error).message || 'Failed to load HubSpot properties for validation');
      toastV4Error(error, 'Could not load the HubSpot fields');
    } finally {
      setIsLoading(false);
    }
  }

  async function loadHubspotData() {
    try {
      setIsLoading(true);
      setPropertiesLoadError(null);

      // Use the page's integration if it passed one, otherwise read it
      const hubspot = integration ?? (await eduleadV4.integrations.get({ id: 'hubspot' })).integration;
      setIsConnected(hubspot?.connected ?? false);

      // If not connected, don't try to load field mappings
      if (!hubspot?.connected) {
        setIsLoading(false);
        return;
      }

      const companyMappings = mappingRecord(hubspot.institution_field_mappings);
      const contactMappings = mappingRecord(hubspot.contact_field_mappings);
      setCompanyFieldMappings(companyMappings);
      setContactFieldMappings(contactMappings);
      setOriginalCompanyFieldMappings(companyMappings);
      setOriginalContactFieldMappings(contactMappings);

      // The HubSpot fields come live from HubSpot: their failure (a revoked token: the API says to
      // connect again) is a toast, not a failed page, and the saved mappings stay valid
      try {
        const properties = await allPages(eduleadV4.integrations.listProperties({ integration_id: 'hubspot', page_size: 500 }));
        setAvailableContactProperties(
          properties.filter(p => p.object_type === 'INTEGRATION_OBJECT_TYPE_CONTACT').map(p => crmPropertyOf(p, 'hubspot'))
        );
        setAvailableCompanyProperties(
          properties.filter(p => p.object_type === 'INTEGRATION_OBJECT_TYPE_INSTITUTION').map(p => crmPropertyOf(p, 'hubspot'))
        );
      } catch (error) {
        setPropertiesLoadError(v4Error(error).message);
        toastV4Error(error, 'Could not load your HubSpot fields');
      }
    } catch (error) {
      toastV4Error(error, 'Failed to load HubSpot field mappings');
    } finally {
      setIsLoading(false);
    }
  }

  async function saveHubspotFieldMappings(changes: MappingChange[]) {
    try {
      setIsSaving(true);

      // Each list with a change is replaced whole by what the editor now shows
      const paths: string[] = [];
      if (changes.some(c => c.objectType === 'CONTACT')) paths.push('contact_field_mappings');
      if (changes.some(c => c.objectType === 'INSTITUTION')) paths.push('institution_field_mappings');
      await eduleadV4.integrations.update({
        id: 'hubspot',
        update_mask: paths.join(','),
        body: {
          contact_field_mappings: mappingList(contactFieldMappings),
          institution_field_mappings: mappingList(companyFieldMappings),
        },
      });
      // Update original state to reflect saved state
      setOriginalCompanyFieldMappings({ ...companyFieldMappings });
      setOriginalContactFieldMappings({ ...contactFieldMappings });
      toast.success('Field mappings saved successfully');
      onSaveSuccess?.();
    } catch (error) {
      toastV4Error(error, 'Failed to save field mappings');
    } finally {
      setIsSaving(false);
    }
  }

  function updateFieldMapping(eduleadField: string, hubspotField: string, isCompanyField: boolean) {
    if (isCompanyField) {
      setCompanyFieldMappings(prev => ({
        ...prev,
        [eduleadField]: hubspotField
      }));
    } else {
      setContactFieldMappings(prev => ({
        ...prev,
        [eduleadField]: hubspotField
      }));
    }
  }

  // Don't render anything if not connected (and we've finished checking)
  if (isConnected === false) {
    return null;
  }

  return (
    <CrmFieldMappings
      title="Field Mappings"
      description="Map Edulead fields to HubSpot properties to enable data synchronization."
      isLoading={isLoading}
      isSaving={isSaving}
      readOnly={readOnly}
      propertiesLoadError={propertiesLoadError}
      companyFields={HUBSPOT_COMPANY_FIELDS}
      contactFields={HUBSPOT_CONTACT_FIELDS}
      availableCompanyProperties={availableCompanyProperties}
      availableContactProperties={availableContactProperties}
      companyFieldMappings={companyFieldMappings}
      contactFieldMappings={contactFieldMappings}
      originalCompanyFieldMappings={originalCompanyFieldMappings}
      originalContactFieldMappings={originalContactFieldMappings}
      onFieldMappingChange={updateFieldMapping}
      onSave={saveHubspotFieldMappings}
      onRefresh={loadHubspotData}
      companyFieldsLabel="Company Fields"
      contactFieldsLabel="Contact Fields"
      placeholder="Select HubSpot field..."
      crmName="HubSpot"
    />
  );
}
